import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CalendarInputCommonProps } from './types';
import { format, isValid, parse, subMilliseconds } from 'date-fns';
import { applyItemInterval, getNewCursorItem, isCursorFocusValid, isCursorItemComplete, isItemValid } from './utils';
import { DAY_RESOLUTION } from './resolution';
import { Input, InputProps } from '../../input';

export interface DateInputProps extends Omit<InputProps, 'value' | 'onChange'>, CalendarInputCommonProps {
  error?: boolean;
  format?: string;
  /**
   * The target properties to write when the user manually types the date.
   */
  writeTarget?: 'from' | 'to' | 'all';
}

export const DateInput: React.FC<DateInputProps> = React.forwardRef(
  (
    {
      format: dateFormat = 'yyyy-MM-dd',
      constraint,
      resolution = DAY_RESOLUTION,
      items,
      cursor,
      onCursorChange,
      onConfirm,
      onCancel,
      target,
      writeTarget,
      error,
      tolerateInvalidValues,
      ...props
    }: DateInputProps,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const currentTarget = target ?? cursor?.focus ?? 'from';
    const focus = currentTarget === 'all' ? 'from' : currentTarget;
    const isFrom = currentTarget === 'from' || currentTarget === 'all';
    const dateRef = useRef<Date | null>(null);
    const [inputValue, setInputValue] = useState('');

    const hoveringValue = useMemo(
      () =>
        isFrom
          ? cursor?.fromHover && format(cursor.fromHover, dateFormat)
          : cursor?.toHover && format(subMilliseconds(cursor.toHover, 1), dateFormat),
      [cursor, isFrom, dateFormat],
    );
    const currentDate = useMemo(
      () => (isFrom ? cursor?.item?.from : cursor?.item?.to && subMilliseconds(cursor.item.to, 1)),
      [cursor, isFrom],
    );
    const currentValue = useMemo(
      () => (currentDate != null ? format(currentDate, dateFormat) : ''),
      [currentDate, dateFormat],
    );

    const isDateValid = useMemo(
      () =>
        currentDate == null ||
        tolerateInvalidValues ||
        isCursorFocusValid(constraint, { items }, cursor, isFrom ? 'from' : 'to'),
      [currentDate, tolerateInvalidValues, constraint, items, cursor, isFrom],
    );

    useEffect(() => {
      if (currentDate?.valueOf() != dateRef.current?.valueOf() || !cursor?.active) {
        dateRef.current = currentDate ?? null;
        setInputValue(currentValue);
        if (cursor != null && cursor.active) {
          onCursorChange({ ...cursor, typing: false, typingInvalid: false });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDate, currentValue, cursor?.active]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        try {
          const parsed = parse(value, dateFormat, new Date());
          if (dateFormat.length > value.length) {
            onCursorChange({ ...cursor, focus, active: true, typing: true, typingInvalid: false });
            return;
          }
          if (dateFormat.length !== value.length || !isValid(parsed)) {
            throw new Error('Invalid time');
          }
          const searchInterval = resolution.resolve(parsed);
          const { item, dateSet } = applyItemInterval(
            cursor?.item ?? getNewCursorItem(),
            constraint,
            { items },
            searchInterval,
            writeTarget ?? currentTarget,
          );
          if (dateSet != null) {
            dateRef.current = dateSet;
          }
          onCursorChange({
            item,
            focus,
            active: true,
            typing: false,
            typingInvalid: false,
            toggled: true,
          });
        } catch {
          onCursorChange({ ...cursor, focus, active: true, typing: true, typingInvalid: true });
          return;
        }
      },
      [dateFormat, resolution, cursor, constraint, items, currentTarget, writeTarget, onCursorChange, focus],
    );
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (
            !cursor?.typing &&
            isCursorItemComplete(cursor?.item) &&
            isItemValid(constraint, { items }, cursor.item)
          ) {
            onConfirm(cursor.item);
          }
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          onCancel?.();
        }
      },
      [constraint, cursor, items, onConfirm, onCancel],
    );
    return (
      <Input
        {...props}
        value={hoveringValue ? '' : cursor?.active ? inputValue : currentValue}
        placeholder={hoveringValue ?? props.placeholder}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        status={!isDateValid || (cursor?.typingInvalid && cursor?.focus === focus) || error ? 'error' : 'info'}
        ref={ref}
      />
    );
  },
);
